'use client';

import { Menu } from '@base-ui/react/menu';
import Link from 'next/link';

import { NAV_GROUPS, type NavColumn } from './nav-data';

const ChevronDown = () => (
  <svg
    aria-hidden
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    className="ml-1 transition-transform group-data-[popup-open]:rotate-180"
  >
    <path
      d="M3 5l3 3 3-3"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function MegaMenuColumns({ columns }: { columns: NavColumn[] }) {
  const colCount = Math.min(columns.length, 4);
  const gridClass = {
    1: 'grid-cols-1',
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-2 lg:grid-cols-3',
    4: 'sm:grid-cols-2 lg:grid-cols-4',
  }[colCount];

  return (
    <div
      data-homepage-mega-menu-columns
      data-column-count={colCount}
      className={`grid gap-x-12 gap-y-8 ${gridClass}`}
    >
      {columns.map((column) => (
        <div key={column.heading} data-homepage-mega-menu-column>
          <h3 data-homepage-mega-menu-heading className="text-sm font-medium text-ink">
            {column.heading}
          </h3>
          <ul data-homepage-mega-menu-links className="mt-4 list-none space-y-3 p-0">
            {column.links.map((link) => (
              <li key={link.href}>
                <Menu.Item
                  data-homepage-mega-menu-link
                  className="-mx-2 block rounded-lg px-2 py-1 text-ink-muted outline-none transition data-[highlighted]:bg-state-hover data-[highlighted]:text-ink"
                  render={<Link href={link.href} prefetch={false} />}
                >
                  {link.label}
                </Menu.Item>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export function DesktopNav() {
  return (
    <nav aria-label="Primary" className="hidden items-center justify-self-end gap-2 md:flex">
      {NAV_GROUPS.map((group) =>
        group.columns ? (
          <Menu.Root key={group.label}>
            <Menu.Trigger className="group inline-flex h-10 min-w-[84px] cursor-pointer items-center justify-center rounded-xl px-3 text-base font-medium text-ink transition hover:bg-state-hover">
              {group.label}
              <ChevronDown />
            </Menu.Trigger>
            <Menu.Portal>
              <Menu.Positioner
                data-homepage-mega-menu-positioner
                sideOffset={12}
                align="start"
                className="z-50"
              >
                <Menu.Popup
                  data-homepage-mega-menu
                  className="min-w-[760px] max-w-[1100px] rounded-2xl border border-line-soft bg-canvas p-8 shadow-lg"
                >
                  <MegaMenuColumns columns={group.columns} />
                </Menu.Popup>
              </Menu.Positioner>
            </Menu.Portal>
          </Menu.Root>
        ) : (
          <Link
            key={group.label}
            href={group.href ?? '#'}
            prefetch={false}
            className="inline-flex h-10 min-w-[84px] items-center justify-center rounded-xl px-3 text-base font-medium text-ink transition hover:bg-state-hover"
          >
            {group.label}
          </Link>
        ),
      )}
    </nav>
  );
}
